/// <amd-module name="CodeCoverage/Common/Sources/CodeCoverageSource" />
import { BuildArtifact } from 'Build/Client/Contracts/Build';
import { IBuildRestClient } from 'Build/Client/RestClient/Build';
import { RunViewArtifact } from 'CodeCoverage/Common/RunViewArtifact';
import { ITestManagementService, TestManagementServiceName } from 'Test/Client/TestManagementServices';
import { BuildCoverage, CodeCoverageSummary, SourceViewBuildCoverage } from 'Test/common-util/Contracts/Test';
import { IVssComponentContext } from 'VSS/Platform/Layout';

interface ApiCallCacheEntry {
    promise: Promise<CodeCoverageSummary | null>,
    complete: boolean,
    value: CodeCoverageSummary | null
}

export class CodeCoverageSource {
    private apiCallCache: {
        [id: number]: ApiCallCacheEntry
    } = {};

    private static _instance: CodeCoverageSource;
    private tcmService: ITestManagementService;

    private constructor(private pageContext: IVssComponentContext) { }

    public static getInstance(pageContext: IVssComponentContext) {
        if (!CodeCoverageSource._instance) {
            CodeCoverageSource._instance = new CodeCoverageSource(pageContext);
        }

        return CodeCoverageSource._instance;
    }

    public getBuildCodeCoverage(runViewArtifact: RunViewArtifact): Promise<BuildCoverage[] | null> {
        let tcmService = this.getTcmService();
        return tcmService.getBuildCodeCoverage(runViewArtifact.buildId, /* moduleCoverageFlag */ 1);
    }

    public getFolderViewBuildCodeCoverage(runViewArtifact: RunViewArtifact): Promise<SourceViewBuildCoverage[]> {
        let tcmService = this.getTcmService();
        return tcmService.getFolderViewCodeCoverage(runViewArtifact.buildId);
    }

    public getBuildArtifacts(runViewArtifact: RunViewArtifact): Promise<BuildArtifact[] | null> {
        let buildRestClient = this.pageContext.pageContext.getRestClient<IBuildRestClient>('IBuildRestClient');
        return buildRestClient.getArtifacts(runViewArtifact.projectId, runViewArtifact.buildId);
    }

    public getCodeCoverageSummary(runViewArtifact: RunViewArtifact): Promise<CodeCoverageSummary | null> {
        let tcmService = this.getTcmService();

        if (this.apiCallCache[runViewArtifact.buildId]) {
            if (this.apiCallCache[runViewArtifact.buildId].complete) {
                return Promise.resolve(this.apiCallCache[runViewArtifact.buildId].value);
            }
            return this.apiCallCache[runViewArtifact.buildId].promise;
        }

        const promise = tcmService.getCodeCoverageSummary(runViewArtifact.buildId);

        this.apiCallCache[runViewArtifact.buildId] = {
            promise: promise,
            complete: false,
            value: null
        };

        const completePromise = (value: any) => {
            this.apiCallCache[runViewArtifact.buildId].complete = true;
            this.apiCallCache[runViewArtifact.buildId].value = value;
        };

        promise.then(completePromise, completePromise);

        return promise;
    }

    private getTcmService(): ITestManagementService {
        if(!this.tcmService) {
            this.tcmService = this.pageContext.pageContext.getService<ITestManagementService>(TestManagementServiceName);
        }

        return this.tcmService;
    }
}
